import { FC } from "react";
import { Stack, Box, Modal, Typography, styled } from "@mui/material";
import { IconClose } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { background, black, divider } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { SharedCaseMatchDetailInfo } from "@/features/sharing/components/SharedCaseMatchDetailInfo";
import { TCaseMatches } from "@/api/services/main/case/dtos/get-case.dto";
import { convertResToFormData } from "@/features/talent/utils/convertTalentFormData";
import { useSharedTalentInfo } from "@/api/services/main/sharing";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { Grid } from "@mui/material";

type TSharedCaseMatchDetailModalProps = {
  isOpen: boolean;
  caseMatch?: TCaseMatches;
  onClose: () => void;
  shareUrl: string;
  casePersonInChargeName?: string;
  talentPersonInChargeName?: string;
};

/** Readonly case match detail modal for shared (public) view */
export const SharedCaseMatchDetailModal: FC<TSharedCaseMatchDetailModalProps> = ({
  isOpen,
  caseMatch,
  onClose,
  shareUrl,
  casePersonInChargeName,
  talentPersonInChargeName: talentPersonInChargeNameProp,
}) => {
  const talentId = caseMatch?.talent?.id;
  const { data: talentData, isLoading: isTalentLoading } = useSharedTalentInfo(talentId, shareUrl);
  const talentFormData = talentData?.data ? convertResToFormData(talentData.data) : undefined;

  // Prefer person-in-charge from fetched talent data (matches original CaseMatchDetailInfo behavior), fallback to prop
  const talentPersonInChargeName = talentFormData?.personInCharge?.fullName ?? talentPersonInChargeNameProp;

  const isLoading = isOpen && caseMatch && talentId && (isTalentLoading || !talentFormData);

  return (
    <Modal
      open={isOpen}
      hideBackdrop={false}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 0,
        "& .MuiBackdrop-root": {
          padding: 0,
        },
      }}
    >
      <ModalContainer>
        <Stack
          direction="row"
          bgcolor={background.disable}
          height="56px"
          minHeight="56px"
          alignItems="center"
          justifyContent="space-between"
          sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px", px: 3 }}
        >
          <Typography variant="body14Light" color={"#0B0C0D"}>
            マッチング情報
          </Typography>
          <IconBox onClick={onClose} sx={{ p: 0 }}>
            <IconClose color={black[80]} fontSize={24} />
          </IconBox>
        </Stack>

        <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", position: "relative" }}>
          {isLoading ? (
            <LoadingArea />
          ) : (
            <Stack sx={{ flex: 1, overflowY: "auto", px: 4, pb: 4, pt: 1 }}>
              {caseMatch ? (
                <Grid container spacing={4}>
                  <Grid item>
                    <SharedCaseMatchDetailInfo
                      caseMatch={caseMatch}
                      talent={talentFormData}
                      casePersonInChargeName={casePersonInChargeName}
                      talentPersonInChargeName={talentPersonInChargeName}
                      shareUrl={shareUrl}
                    />
                  </Grid>
                </Grid>
              ) : null}
            </Stack>
          )}
        </Box>

        <Box>
          <hr color={divider.high} />
          <Stack direction="row" gap="16px" justifyContent="flex-end" p="16px 32px">
            <Button variant="ghost" size="sm" onClick={onClose} sx={{ width: "102px" }}>
              閉じる
            </Button>
          </Stack>
        </Box>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  width: 764px;
  height: 728px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  outline: none;
  position: relative;
  margin: auto;
  &:focus {
    outline: none;
  }
`;
